/**
 * Architecture Validation & Testing Utilities
 */

import {
  ArchitectureRule,
  RuleViolation,
  RuleSeverity,
  ExtendedArchiMateDiagram,
} from './extended-types';
import { ArchiMateElement, ArchiMateRelationship } from './types';
import { uuidv7 } from 'uuidv7';

/**
 * Built-in validation rules
 */

export const BUILT_IN_RULES: ArchitectureRule[] = [
  {
    id: 'no-layer-skipping',
    name: 'No Layer Skipping',
    description: 'Elements should not skip layers (e.g., Business cannot directly access Technology)',
    severity: 'error',
    enabled: true,
    category: 'layering',
    check: checkNoLayerSkipping,
  },
  {
    id: 'all-services-monitored',
    name: 'All Services Must Be Monitored',
    description: 'Every Application Service must have monitoring',
    severity: 'warning',
    enabled: true,
    category: 'operations',
    check: checkAllServicesMonitored,
  },
  {
    id: 'no-orphaned-elements',
    name: 'No Orphaned Elements',
    description: 'All elements should have at least one relationship',
    severity: 'info',
    enabled: true,
    category: 'completeness',
    check: checkNoOrphanedElements,
  },
  {
    id: 'pii-in-public-zone',
    name: 'No PII in Public Zone',
    description: 'PII data cannot be stored in Public security zone',
    severity: 'critical',
    enabled: true,
    category: 'security',
    check: checkNoPIIInPublicZone,
  },
  {
    id: 'unencrypted-sensitive-data',
    name: 'Sensitive Data Must Be Encrypted',
    description: 'PII and confidential data must be encrypted at rest and in transit',
    severity: 'critical',
    enabled: true,
    category: 'security',
    check: checkSensitiveDataEncryption,
  },
  {
    id: 'high-technical-debt',
    name: 'High Technical Debt',
    description: 'Flag elements with technical debt score > 7',
    severity: 'warning',
    enabled: true,
    category: 'technical-debt',
    check: checkHighTechnicalDebt,
  },
  {
    id: 'missing-ownership',
    name: 'Missing Ownership',
    description: 'All elements should have assigned ownership',
    severity: 'warning',
    enabled: true,
    category: 'governance',
    check: checkMissingOwnership,
  },
  {
    id: 'missing-documentation',
    name: 'Missing Documentation',
    description: 'All elements should have documentation',
    severity: 'info',
    enabled: true,
    category: 'documentation',
    check: checkMissingDocumentation,
  },
  {
    id: 'circular-dependencies',
    name: 'No Circular Dependencies',
    description: 'Detect circular dependency chains',
    severity: 'error',
    enabled: true,
    category: 'structure',
    check: checkCircularDependencies,
  },
  {
    id: 'single-point-of-failure',
    name: 'Single Point of Failure',
    description: 'Components with no redundancy (max instances = 1)',
    severity: 'warning',
    enabled: true,
    category: 'reliability',
    check: checkSinglePointOfFailure,
  },
];

// Rule implementation functions

function checkNoLayerSkipping(diagram: ExtendedArchiMateDiagram): RuleViolation[] {
  const violations: RuleViolation[] = [];
  const layerOrder = ['strategy', 'business', 'application', 'technology', 'physical'];

  diagram.relationships.forEach((rel) => {
    const source = diagram.elements.find((e) => e.id === rel.sourceId);
    const target = diagram.elements.find((e) => e.id === rel.targetId);

    if (!source || !target) return;

    const sourceLayerIndex = layerOrder.indexOf(source.layer);
    const targetLayerIndex = layerOrder.indexOf(target.layer);

    // Check if layers are more than 1 apart
    if (Math.abs(sourceLayerIndex - targetLayerIndex) > 1) {
      violations.push({
        ruleId: 'no-layer-skipping',
        ruleName: 'No Layer Skipping',
        severity: 'error',
        message: `${source.name} (${source.layer}) should not directly connect to ${target.name} (${target.layer})`,
        elementIds: [source.id, target.id],
        relationshipIds: [rel.id],
        suggestion: 'Add an intermediate layer element',
      });
    }
  });

  return violations;
}

function checkAllServicesMonitored(diagram: ExtendedArchiMateDiagram): RuleViolation[] {
  const violations: RuleViolation[] = [];

  const services = diagram.elements.filter((e) => e.type === 'application-service');

  services.forEach((service) => {
    const performance = diagram.elementMetadata[service.id]?.performance;

    if (!performance || !performance.slo.availability) {
      violations.push({
        ruleId: 'all-services-monitored',
        ruleName: 'All Services Must Be Monitored',
        severity: 'warning',
        message: `Service "${service.name}" has no availability SLO defined`,
        elementIds: [service.id],
        relationshipIds: [],
        suggestion: 'Add availability SLO and monitoring configuration',
      });
    }
  });

  return violations;
}

function checkNoOrphanedElements(diagram: ExtendedArchiMateDiagram): RuleViolation[] {
  const violations: RuleViolation[] = [];

  diagram.elements.forEach((element) => {
    const hasRelationship = diagram.relationships.some(
      (rel) => rel.sourceId === element.id || rel.targetId === element.id
    );

    if (!hasRelationship) {
      violations.push({
        ruleId: 'no-orphaned-elements',
        ruleName: 'No Orphaned Elements',
        severity: 'info',
        message: `Element "${element.name}" has no relationships`,
        elementIds: [element.id],
        relationshipIds: [],
        suggestion: 'Connect this element or remove it if unused',
      });
    }
  });

  return violations;
}

function checkNoPIIInPublicZone(diagram: ExtendedArchiMateDiagram): RuleViolation[] {
  const violations: RuleViolation[] = [];

  diagram.elements.forEach((element) => {
    const security = diagram.elementMetadata[element.id]?.security;

    if (security?.zone === 'public' && security?.dataClassification === 'pii') {
      violations.push({
        ruleId: 'pii-in-public-zone',
        ruleName: 'No PII in Public Zone',
        severity: 'critical',
        message: `PII data in "${element.name}" is in Public security zone`,
        elementIds: [element.id],
        relationshipIds: [],
        suggestion: 'Move to Restricted or Critical zone',
      });
    }
  });

  return violations;
}

function checkSensitiveDataEncryption(diagram: ExtendedArchiMateDiagram): RuleViolation[] {
  const violations: RuleViolation[] = [];

  diagram.elements.forEach((element) => {
    const security = diagram.elementMetadata[element.id]?.security;

    if (
      security &&
      (security.dataClassification === 'pii' || security.dataClassification === 'confidential')
    ) {
      if (!security.encryption.atRest) {
        violations.push({
          ruleId: 'unencrypted-sensitive-data',
          ruleName: 'Sensitive Data Must Be Encrypted',
          severity: 'critical',
          message: `Sensitive data in "${element.name}" is not encrypted at rest`,
          elementIds: [element.id],
          relationshipIds: [],
          suggestion: 'Enable encryption at rest',
        });
      }

      if (!security.encryption.inTransit) {
        violations.push({
          ruleId: 'unencrypted-sensitive-data',
          ruleName: 'Sensitive Data Must Be Encrypted',
          severity: 'critical',
          message: `Sensitive data in "${element.name}" is not encrypted in transit`,
          elementIds: [element.id],
          relationshipIds: [],
          suggestion: 'Enable TLS/encryption in transit',
        });
      }
    }
  });

  return violations;
}

function checkHighTechnicalDebt(diagram: ExtendedArchiMateDiagram): RuleViolation[] {
  const violations: RuleViolation[] = [];

  diagram.elements.forEach((element) => {
    const debt = diagram.elementMetadata[element.id]?.debt;

    if (debt && debt.score > 7) {
      violations.push({
        ruleId: 'high-technical-debt',
        ruleName: 'High Technical Debt',
        severity: 'warning',
        message: `"${element.name}" has high technical debt (${debt.score}/10): ${debt.description}`,
        elementIds: [element.id],
        relationshipIds: [],
        suggestion: `Allocate ${debt.estimatedEffort} to address this debt`,
      });
    }
  });

  return violations;
}

function checkMissingOwnership(diagram: ExtendedArchiMateDiagram): RuleViolation[] {
  const violations: RuleViolation[] = [];

  diagram.elements.forEach((element) => {
    const ownership = diagram.elementMetadata[element.id]?.ownership;

    if (!ownership) {
      violations.push({
        ruleId: 'missing-ownership',
        ruleName: 'Missing Ownership',
        severity: 'warning',
        message: `"${element.name}" has no assigned owner`,
        elementIds: [element.id],
        relationshipIds: [],
        suggestion: 'Assign a team and owner',
      });
    }
  });

  return violations;
}

function checkMissingDocumentation(diagram: ExtendedArchiMateDiagram): RuleViolation[] {
  const violations: RuleViolation[] = [];

  diagram.elements.forEach((element) => {
    if (!element.documentation || element.documentation.trim() === '') {
      violations.push({
        ruleId: 'missing-documentation',
        ruleName: 'Missing Documentation',
        severity: 'info',
        message: `"${element.name}" has no documentation`,
        elementIds: [element.id],
        relationshipIds: [],
        suggestion: 'Add description and purpose',
      });
    }
  });

  return violations;
}

function checkCircularDependencies(diagram: ExtendedArchiMateDiagram): RuleViolation[] {
  const violations: RuleViolation[] = [];

  // Build adjacency list
  const graph = new Map<string, string[]>();
  diagram.elements.forEach((element) => {
    graph.set(element.id, []);
  });

  diagram.relationships.forEach((rel) => {
    if (!graph.has(rel.sourceId)) graph.set(rel.sourceId, []);
    graph.get(rel.sourceId)!.push(rel.targetId);
  });

  // Detect cycles using DFS
  const visited = new Set<string>();
  const recursionStack = new Set<string>();

  function dfs(nodeId: string, path: string[]): void {
    visited.add(nodeId);
    recursionStack.add(nodeId);

    const neighbors = graph.get(nodeId) || [];
    for (const neighbor of neighbors) {
      if (!visited.has(neighbor)) {
        dfs(neighbor, [...path, neighbor]);
      } else if (recursionStack.has(neighbor)) {
        // Cycle detected
        const cycleStart = path.indexOf(neighbor);
        const cycle = path.slice(cycleStart);

        const elements = cycle
          .map((id) => diagram.elements.find((e) => e.id === id))
          .filter((e): e is ArchiMateElement => e !== undefined);

        violations.push({
          ruleId: 'circular-dependencies',
          ruleName: 'No Circular Dependencies',
          severity: 'error',
          message: `Circular dependency detected: ${elements.map((e) => e.name).join(' → ')}`,
          elementIds: cycle,
          relationshipIds: [],
          suggestion: 'Break the cycle by removing or reversing a dependency',
        });
      }
    }

    recursionStack.delete(nodeId);
  }

  diagram.elements.forEach((element) => {
    if (!visited.has(element.id)) {
      dfs(element.id, [element.id]);
    }
  });

  return violations;
}

function checkSinglePointOfFailure(diagram: ExtendedArchiMateDiagram): RuleViolation[] {
  const violations: RuleViolation[] = [];

  diagram.elements.forEach((element) => {
    const performance = diagram.elementMetadata[element.id]?.performance;

    if (
      performance &&
      performance.scalability.max === 1 &&
      !performance.scalability.autoScale
    ) {
      // Check if this element is critical (many dependencies)
      const incomingRels = diagram.relationships.filter((rel) => rel.targetId === element.id);

      if (incomingRels.length > 3) {
        violations.push({
          ruleId: 'single-point-of-failure',
          ruleName: 'Single Point of Failure',
          severity: 'warning',
          message: `"${element.name}" is a single point of failure (${incomingRels.length} dependencies, no redundancy)`,
          elementIds: [element.id],
          relationshipIds: [],
          suggestion: 'Add redundancy or enable auto-scaling',
        });
      }
    }
  });

  return violations;
}

/**
 * Rule validation engine
 */

export function validateDiagram(diagram: ExtendedArchiMateDiagram): RuleViolation[] {
  const enabledRules = [...BUILT_IN_RULES, ...diagram.rules].filter((rule) => rule.enabled);

  const violations: RuleViolation[] = [];

  enabledRules.forEach((rule) => {
    try {
      const ruleViolations = rule.check(diagram);
      violations.push(...ruleViolations);
    } catch (error) {
      console.error(`Error running rule "${rule.name}":`, error);
    }
  });

  return violations;
}

export function getViolationsBySeverity(
  violations: RuleViolation[],
  severity: RuleSeverity
): RuleViolation[] {
  return violations.filter((v) => v.severity === severity);
}

export function getViolationsByCategory(
  diagram: ExtendedArchiMateDiagram,
  category: string
): RuleViolation[] {
  const violations = validateDiagram(diagram);
  const rules = [...BUILT_IN_RULES, ...diagram.rules];

  return violations.filter((violation) => {
    const rule = rules.find((r) => r.id === violation.ruleId);
    return rule?.category === category;
  });
}

export function generateValidationReport(diagram: ExtendedArchiMateDiagram): string {
  const violations = validateDiagram(diagram);

  let report = `# Architecture Validation Report\n\n`;
  report += `## ${diagram.name}\n\n`;
  report += `**Generated:** ${new Date().toISOString().split('T')[0]}\n\n`;

  // Summary
  const critical = getViolationsBySeverity(violations, 'critical');
  const errors = getViolationsBySeverity(violations, 'error');
  const warnings = getViolationsBySeverity(violations, 'warning');
  const info = getViolationsBySeverity(violations, 'info');

  report += `## Summary\n\n`;
  report += `- 🔴 **Critical:** ${critical.length}\n`;
  report += `- ⛔ **Errors:** ${errors.length}\n`;
  report += `- ⚠️ **Warnings:** ${warnings.length}\n`;
  report += `- ℹ️ **Info:** ${info.length}\n\n`;

  // Violations by severity
  if (critical.length > 0) {
    report += `## 🔴 Critical Violations\n\n`;
    critical.forEach((v) => {
      report += `### ${v.ruleName}\n`;
      report += `${v.message}\n`;
      if (v.suggestion) report += `**Suggestion:** ${v.suggestion}\n`;
      report += `\n`;
    });
  }

  if (errors.length > 0) {
    report += `## ⛔ Errors\n\n`;
    errors.forEach((v) => {
      report += `- **${v.ruleName}**: ${v.message}\n`;
    });
    report += `\n`;
  }

  if (warnings.length > 0) {
    report += `## ⚠️ Warnings\n\n`;
    warnings.forEach((v) => {
      report += `- **${v.ruleName}**: ${v.message}\n`;
    });
    report += `\n`;
  }

  if (info.length > 0 && info.length <= 10) {
    report += `## ℹ️ Info\n\n`;
    info.forEach((v) => {
      report += `- ${v.message}\n`;
    });
  } else if (info.length > 10) {
    report += `## ℹ️ Info\n\n`;
    report += `${info.length} informational items (not listed)\n`;
  }

  return report;
}

export function calculateComplianceScore(diagram: ExtendedArchiMateDiagram): number {
  const violations = validateDiagram(diagram);

  let totalElements = diagram.elements.length;
  if (totalElements === 0) return 100;

  // Weighted deductions
  const critical = getViolationsBySeverity(violations, 'critical').length * 20;
  const errors = getViolationsBySeverity(violations, 'error').length * 10;
  const warnings = getViolationsBySeverity(violations, 'warning').length * 5;

  const deduction = critical + errors + warnings;
  const score = Math.max(0, 100 - deduction);

  return score;
}

export function createCustomRule(
  name: string,
  description: string,
  severity: RuleSeverity,
  category: string,
  checkFunction: (diagram: ExtendedArchiMateDiagram) => RuleViolation[]
): ArchitectureRule {
  return {
    id: uuidv7(),
    name,
    description,
    severity,
    enabled: true,
    category,
    check: checkFunction,
  };
}
